// src/app/core/services/spinner.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, defer, of, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap, finalize, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SpinnerService {

    private readonly _pending = new BehaviorSubject<number>(0);

    // Anti-flicker: delay showing a bit
    private readonly showDelayMs = 0;

    // Keep it visible at least this long once it appears
    private readonly minVisibleMs = 250;

    // Track when the spinner actually became visible
    private _lastOnAt: number | null = null;

    readonly spinning$: Observable<boolean> = this._pending.pipe(
        map(n => n > 0),
        distinctUntilChanged(),
        //switchMap(on => on ? timer(this.showDelayMs).pipe(map(() => true)) : of(false))
        switchMap(on => {
        if (on) {
            // Delay showing; when it shows, record the timestamp
            return timer(this.showDelayMs).pipe(
            tap(() => { this._lastOnAt = Date.now(); }),
            map(() => true)
            );
        } else {
            // Ensure it's been visible for at least minVisibleMs
            const elapsed = this._lastOnAt ? (Date.now() - this._lastOnAt) : Number.POSITIVE_INFINITY;
            const holdMs = Math.max(0, this.minVisibleMs - elapsed);
            return timer(holdMs).pipe(
            tap(() => { this._lastOnAt = null; }),
            map(() => false)
            );
        }
        })
    );

    inc(): void {
    
        this._pending.next(this._pending.value + 1); 
    }

    dec(): void {
    
        this._pending.next(Math.max(0, this._pending.value - 1)); 
    }

    reset(): void {
    
        this._pending.next(0); 
        this._lastOnAt = null;
    }

    // Handy for non-HTTP async work:
    track<T>(source$: Observable<T>): Observable<T> {

        return defer(() => {
            this.inc();
            return source$.pipe(finalize(() => this.dec()));
        });
    }
}

/*
Opt out example

import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_SPINNER } from '../core/services/spinner.tokens';

this.http.get('/api/report', {
  context: new HttpContext().set(SKIP_SPINNER, true)
}).subscribe();
*/